import {UserAction, UserActionTypes} from "./UserActions";
import {
    AccountBalance,
    AppMode,
    BonusOffer,
    KycStatus,
    LoginResponseStatus,
    ManualLoginRequest,
    MultiFactorAuthProvider,
    NextUpcomingCashback,
    RegistrationRequest,
    RegistrationResponse,
    UserBonusView,
    UserInfo,
    UserLevelView,
    UserMessage
} from "../http/protocol";
import {LoginStatus, RequestCashbackStatus, ResetPasswordStatus, VerifyPrincipalStatus} from "../http/Types";
import {DepositActions, DepositActionType} from "../payments/DepositActions";
import {getContainer} from "../ioc/IOCSetup";
import {addToast} from "../toasts/toasts";
import {t} from "../i18n";
import {ResetPasswordChannel} from "./UserService";

export enum LoginError {
    INVALID_USER_OR_PASSWORD,
    USER_BLOCKED,
    TOO_MANY_LOGIN_ATTEMPTS,
    FB_EMAIL_ALREADY_EXIST,
    INVALID_TOKEN

}
export enum FacebookStatus {
    NONE, IN_PROGRESS
}

export enum LoginType {
    EMAIL,
    FACEBOOK,
    TOKEN,
    PHONE
}
export interface MFAState {
    username: string;
    password: string;
    provider: MultiFactorAuthProvider;
}
// Temp for bet88 Audit metamap
export type TempMetamap = { user: RegistrationRequest }

export interface UserState {
    status? : LoginStatus;
    facebookStatus? :FacebookStatus
    loginError?: LoginError;
    user? : UserInfo;
    displayLogin? : boolean;
    displayMenuMobile? : boolean;
    displayBalanceDropdown? : boolean;
    initialBalanceFetched? : boolean;
    balances: AccountBalance[];
    cashbacks?: NextUpcomingCashback[];
    requestCachbackStatus?: RequestCashbackStatus;
    cashbacksError?: Error | string;
    balanceError?: Error;
    registration? : RegistrationResponse;
    resetPasswordStatus? : ResetPasswordStatus;
    resetPasswordChannel? : ResetPasswordChannel;
    resetPasswordAddress? : string;
    verifyEmailStatus? : VerifyPrincipalStatus;
    manualLoginRequest? : ManualLoginRequest;
    activeCurrency? : string;
    showBalance?: boolean;
    metamapStatus: KycStatus;
    appMode?: AppMode;
    showPushPrompt?:boolean;
    showAppInstallGuide?:boolean;
    bonusOffers?:BonusOffer[];
    activeBonuses?:UserBonusView[];
    mfa? : MFAState;
    userInfoLoaded?: boolean;
    userLevelView?: UserLevelView;
    versionToAccept?: string;
    newDevice?: boolean;
    balanceErrorCount? : number;
    balanceUpdating? : boolean;
    modal: {verification: boolean, newUser: boolean},
    temp?: TempMetamap // bet88 audit
    // isHomeScreen: boolean;
    unreadMessages? : number;
    toasts? : UserMessage[];
}




function userSettingsReducer(state: { [index: string]: string } = {}, action: UserAction) {
    switch (action.type) {
        case UserActionTypes.USERSETTINGS_RESPONSE:
            return {...state, ...action.response}
        default:
            return state;
    }
}
export const LAST_SELECTED_CURRENCY_KEY = "lastSelectedCurrency";
export const setStorageCurrency = (code? : string) =>  {
    if(!code) {
        return;
    }
    try {
        localStorage.setItem(LAST_SELECTED_CURRENCY_KEY, code);
    } catch (e) {
        console.log("Unable to read from localStorage",e);
    }
}
export const getStorageCurrency = () => {
    try {
        return localStorage.getItem(LAST_SELECTED_CURRENCY_KEY);
    } catch (e) {
        console.log("Unable to read from localStorage",e);
        return null;
    }
}
export const getActiveCurrency = (state : UserState, balances : AccountBalance[], isLogin : boolean = false) => {
    //if currency is defined in state we assume it is already correct
    if(state.activeCurrency) {
        return state.activeCurrency;
    }
    let ac = undefined;
    let settingsCurrencies = getContainer().getSettingsService().getSettingsData()?.currencies;

    let currencies = settingsCurrencies?.filter(c => c.visible).filter(c => {
        return typeof(balances.find(b => b.balance?.currencyCode == c.code)) != "undefined";
    });

    let localStorageCurrency : string | null =  getStorageCurrency();

    //if not defined in state and not previously manually selected we check preferredCurrency
    if(isLogin && currencies && state.user?.attributes?.["preferredCurrency"]) {
        let preferred = currencies.find(c => c.code == state.user?.attributes?.["preferredCurrency"]);
        if(preferred) {
            ac = preferred.code;
            setStorageCurrency(ac);
        }
    }

    //if not in state check last selected currency in local storage
    if(!ac && localStorageCurrency && currencies) {
        let lastSelected = currencies.find(c => c.code == localStorageCurrency);
        if(lastSelected) {
            ac = lastSelected.code;
        }
    }

    //take fist currency
    if(!ac && currencies) {
        ac = currencies[0].code;
        setStorageCurrency(ac);
    }

    return ac;
}

const updateCurrencies = (currencies: AccountBalance[], currentCurrencies: AccountBalance[]): AccountBalance[] => {
    if(!currencies || currencies.length < 1) return currentCurrencies;

    const updatedCurrencies = [...currentCurrencies];

    for(const currency of currencies) {
        const existing = updatedCurrencies.find(c => c.balance?.currencyCode === currency.balance?.currencyCode);
        if(existing) {
            existing.balance = currency.balance;
        } else {
            updatedCurrencies.push(currency);
        }
    }

    return updatedCurrencies;
}

const initialState : UserState = {
    newDevice : false,
    initialBalanceFetched : false,
    status : LoginStatus.INITIAL_NOT_AUTHENTICATED,
    displayLogin : false,
    displayMenuMobile : false,
    displayBalanceDropdown : false,
    showBalance: true,
    facebookStatus : FacebookStatus.NONE,
    metamapStatus: KycStatus.NOT_STARTED,
    appMode : AppMode.NONE,
    showPushPrompt: false,
    showAppInstallGuide:false,
    userLevelView: {},
    balanceErrorCount : 0,
    balanceUpdating : false,
    modal: {verification: false, newUser: false},
    balances: [],
    cashbacks: [],
    // isHomeScreen: false,
    unreadMessages: 0,
    toasts: [],
};

export function userReducer(state: UserState = initialState, action : UserAction|DepositActions ) {
    switch (action.type) {
        case UserActionTypes.SHOW_LOGIN:
            return {...state, displayLogin : action.show}
        case UserActionTypes.SHOW_MENU_MOBILE:
            return {...state, displayMenuMobile : action.show}
        case UserActionTypes.SHOW_BALANCE_DROPDOWN:
            return {...state, displayBalanceDropdown : action.show}
        case UserActionTypes.LOGIN_REQUEST:
            return {...state, status : LoginStatus.IN_PROGRESS};
        case UserActionTypes.MFA_VERIFICATION:
            return {...state, status : LoginStatus.MFA_VERIFICATION_REQUIRED, mfa : { provider : action.provider, username : action.username, password : action.password }}
        case UserActionTypes.MFA_VERIFICATION_ERROR:
            return {...state, status : LoginStatus.MFA_VERIFICATION_ERROR};
        case UserActionTypes.MFA_RESET:
            return {...state, status : LoginStatus.INITIAL_NOT_AUTHENTICATED, mfa : undefined }
        case UserActionTypes.MANUAL_APPROVAL_REQUIRED:
            return {...state, status : LoginStatus.MANUAL_APPROVAL_REQUIRED }
        case UserActionTypes.CLEAR_MANUAL_APPROVAL:
            return {...state, status : LoginStatus.INITIAL_NOT_AUTHENTICATED }
        case UserActionTypes.LOGIN_RESPONSE:
            let loginStatus = LoginStatus.LOGGED_IN;
            let modal = state.modal;
            if(action.response.responseStatus === LoginResponseStatus.VERIFICATION_REQUIRED) {
                loginStatus = LoginStatus.VERIFICATION_REQUIRED;
            } else {
                modal = {verification: false, newUser: false};
            }
            return {...state, status : loginStatus, user : action.response, displayLogin : false, modal : modal,  facebookStatus : FacebookStatus.NONE, mfa : undefined, manualLoginRequest: undefined, versionToAccept: undefined};
        case UserActionTypes.LOGIN_ERROR:
            return {...state, status : LoginStatus.LOGIN_ERROR, loginError : action.error, facebookStatus : FacebookStatus.NONE };
        case UserActionTypes.LOGOUT:
            return {...state, status : LoginStatus.LOGGED_OUT, newDevice : false, balances : [], user : undefined, registration: undefined, verifyEmailStatus : VerifyPrincipalStatus.NOT_USED,
                metamapStatus: KycStatus.NOT_STARTED, activeCurrency : undefined, displayMenuMobile : false};
        case UserActionTypes.BALANCE_REQUEST:
            return {...state, balanceUpdating : true};
        case UserActionTypes.BALANCE_RESPONSE:
            try {
                const updatedActiveBonuses = action.response.activeBonuses

                if (state.activeBonuses  &&  state.activeBonuses.length > 0  &&  updatedActiveBonuses  &&  updatedActiveBonuses.length > 0) {
                    const bonus = state.activeBonuses[0]
                    const updatedBonus = updatedActiveBonuses[0]
                    if (bonus.bonusConfig?.type == "PARACHUTE") {
                        if (!bonus.parachuteBonusPhase  &&  updatedBonus.parachuteBonusPhase) {
                            addToast(() => {}, t('bonus.bonusPhaseNotification.title'), t('bonus.bonusPhaseNotification.message'), undefined, 10000)
                        }
                    }
                }
            } catch (error) {
                console.error(error)
            }
            const balances = updateCurrencies(action.response.balances, state.balances);

            return {...state, toasts : action.response.messages, unreadMessages : action.response.unreadMessages, initialBalanceFetched : true, balanceUpdating : false, bonusOffers:action.response.bonusOffers, activeBonuses:action.response.activeBonuses, balances, balanceError: undefined, balanceErrorCount : 0, activeCurrency : getActiveCurrency(state, action.response.balances, action.login) };
        case UserActionTypes.BALANCE_ERROR:
            return {...state, balanceUpdating : false, balanceError : action.error, balanceErrorCount : (state.balanceErrorCount ?? 0) + 1 }
        case UserActionTypes.USER_INFO_RESPONSE:
            let status = action.info.loggedIn == true ? LoginStatus.LOGGED_IN : LoginStatus.INITIAL_NOT_AUTHENTICATED;
            return {...state,  user : action.info, status : status, displayLogin : false, userInfoLoaded : true};
        case UserActionTypes.REGISTER_RESPONSE:
            return {...state, registration : action.response};
        case UserActionTypes.REQUEST_RECOVER_PASSWORD:
            return {...state, resetPasswordStatus : ResetPasswordStatus.IN_PROGRESS, resetPasswordChannel: action.channel, resetPasswordAddress: action.address};
        case UserActionTypes.REQUEST_RECOVER_PASSWORD_ERROR:
            return {...state, resetPasswordStatus : ResetPasswordStatus.ERROR};
        case UserActionTypes.RESET_PASSWORD_REQUEST:
            return {...state, resetPasswordStatus : ResetPasswordStatus.IN_PROGRESS};
        case UserActionTypes.RESET_PASSWORD_RESPONSE:
            return {...state, resetPasswordStatus : ResetPasswordStatus.SENT};
        case UserActionTypes.RESET_PASSWORD_ERROR:
            return {...state, resetPasswordStatus : ResetPasswordStatus.ERROR};
        case UserActionTypes.RESET_PASSWORD_CLEAR:
            return {...state, resetPasswordStatus : undefined};
        case UserActionTypes.SET_ACTIVE_CURRENCY:
            return {...state, activeCurrency : action.activeCurrency};
        case UserActionTypes.SET_SHOW_BALANCE:
            return {...state, showBalance : action.show};
        case UserActionTypes.VERIFY_EMAIL_REQUEST:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.IN_PROGRESS, status : LoginStatus.VERIFICATION_REQUIRED, newDevice : action.newDevice};
        case UserActionTypes.VERIFY_EMAIL_RESPONSE:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.READY, manualLoginRequest: action.request};
        case UserActionTypes.VERIFY_EMAIL_ERROR:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.ERROR};
        case UserActionTypes.VERIFY_CHANNEL_CODE_REQUEST:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.IN_PROGRESS};
        case UserActionTypes.VERIFY_CHANNEL_CODE_RESPONSE:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.COMPLETE, manualLoginRequest: undefined};
        case UserActionTypes.VERIFY_CHANNEL_CODE_ERROR:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.ERROR};
        case UserActionTypes.VERIFY_RESET:
            return {...state, verifyEmailStatus : VerifyPrincipalStatus.NOT_USED};
        case UserActionTypes.TERMS_VERSION_ACCEPT_REQUIRED:
            return {...state, versionToAccept: action.versionToAccept, manualLoginRequest: action.request, status: LoginStatus.ACCEPT_TERMS_REQUIRED};
        case UserActionTypes.TERMS_VERSION_DECLINED:
            return {...state, versionToAccept: undefined, manualLoginRequest: undefined, status: LoginStatus.INITIAL_NOT_AUTHENTICATED};
        case UserActionTypes.FACEBOOK_LOGIN_START:
            return {...state, facebookStatus : FacebookStatus.IN_PROGRESS};
        case UserActionTypes.FACEBOOK_LOGIN_CANCELED:
            return {...state, facebookStatus : FacebookStatus.NONE};
        case UserActionTypes.FACEBOOK_LOGIN_RESPONSE:
            return {...state, facebookStatus : FacebookStatus.IN_PROGRESS};
        case UserActionTypes.SET_MATI_STATUS:
            return {...state, metamapStatus: action.status};
        case UserActionTypes.SET_APP_MODE:
            const am = action.appMode || AppMode.NONE
            console.log("Setting app mode = ", am);
            return {...state, appMode: am};
        case UserActionTypes.USERSETTINGS_RESPONSE:
            if(state.user) {
                return userSettingsReducer(state.user&& state.user.userSettings, action);
            } else return;
        case DepositActionType.INIT_DEPOSIT_REQUEST:
            if(state.user && state.user.userSettings) {
                let req = (action as any).request;
                if(req && req.amount && req.activeCurrency) {
                    let userSettings = {...state.user.userSettings};
                    userSettings["last_deposit_amount_"+req.activeCurrency] = req.amount;
                    return {...state, user : {...state.user, userSettings : userSettings}}
                }
            }
            return state;
        case UserActionTypes.SHOW_IOS_INSTALL_GUIDE:
            return {...state, showAppInstallGuide:action.show}
        case UserActionTypes.SHOW_PUSH_PROMPT:
            return {...state, showPushPrompt : action.show};
        case UserActionTypes.REDIRECT_USER:
            // history.pushState({},"",action.url);
            // history.go();
            return ;
        case UserActionTypes.LEVEL_VIEW_RESPONSE:
            return {...state, userLevelView : action.response};
        case UserActionTypes.SET_HOMESCREEN:
            return {...state, isHomeScreen : action.homeScreen};
        case UserActionTypes.SET_USER_MODAL:
            console.log(action)
            return {...state, modal: { verification: action.verification, newUser: action.newUser}};
        case UserActionTypes.CASHBACK_REQUEST:
            return {...state, requestCachbackStatus : RequestCashbackStatus.IN_PROGRESS};
        case UserActionTypes.CASHBACK_RESPONSE:
            return {...state, cashbacks: action.response, requestCachbackStatus : RequestCashbackStatus.SUCCESS};
        case UserActionTypes.CASHBACK_ERROR:
            return {...state, cashbacksError: action.error, requestCachbackStatus : RequestCashbackStatus.ERROR};
        case UserActionTypes.TEMP:
            return {...state, temp: { user: action.temp } };
        default:
            return state;
    }
}
